<template>
    <div class="relative inline-flex items-center" ref="triggerElement" v-if="!props.hidden">
        <Tooltip :text="props.tooltipText || 'Transpose'" :position="props.tooltipPosition || 'bottom'">
            <button 
                @click="handleToggle" 
                data-transpose-button
                class="cursor-pointer menu-list-item w-[40px] h-[40px] flex items-center justify-center focus:outline-none transition-colors"
                :class="[
                    props.theme || 'light',
                    {'opacity-50 cursor-not-allowed': props.disabled}
                ]"
                :title="props.tooltipText || 'Transpose'">
                
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"></path>
                </svg>
            </button>
        </Tooltip>
        <Teleport to="body">
            <Transition name="fade">
                <div 
                    v-if="isOpen" 
                    ref="dropdownElement"
                    data-transpose-dropdown
                    class="fixed w-56 p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl transition-all duration-200"
                    :style="dropdownStyle"
                    @click.stop
                >
                    <Transpose ref="transposeComponent" class="print:hidden" @onTranspose="onTranspose" />
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, defineProps, defineExpose, defineEmits, watch } from 'vue'
import Transpose from './TriadTranspose.vue'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import { useTeleportDropdown } from '@/composables/useTeleportDropdown.js'

const props = defineProps({
    disabled: Boolean,
    position: String,
    theme: {
        type: String,
        default: 'light'
    },
    tooltipText: String,
    tooltipPosition: String,
    hidden: Boolean,
    zIndex: {
        type: [String, Number],
        default: '200'
    }
})
const emits = defineEmits(['onTranspose'])

const transposeComponent = ref(null)

// Determine position based on prop - use 'top' if prop is 'top', otherwise 'bottom'
const dropdownPosition = props.position === 'top' ? 'top' : 'bottom'

const {
    isOpen,
    triggerElement,
    dropdownElement,
    dropdownStyle,
    toggle,
    close
} = useTeleportDropdown({
    position: dropdownPosition,
    zIndex: String(props.zIndex || '200')
})

const handleToggle = () => {
    if (props.disabled) return
    toggle()
}

// Watch for disabled prop changes
watch(() => props.disabled, (value) => {
    if (value) {
        close()
    }
})

// Expose the transpose component ref and updateSemitone method for external access
const updateSemitone = (value) => {
    if (transposeComponent.value) {
        transposeComponent.value.updateSemitone(value)
    }
}

const onTranspose = (value) => {
    emits('onTranspose', value)
}

defineExpose({ transposeComponent, updateSemitone })
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>